import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import logoDark from "../../../assets/images/CRMit_logo_Dark.png";
import { useBranding } from "../../branding/Branding";
import "./authShell.scss";

/**
 * AuthShell — the shared, branded frame for every public auth page
 * (login / forgot / reset / register / verify / invite).
 *
 * Replaces the stock Velzon `ParticlesAuth` background with the app's
 * design-system gradient (indigo → violet → sky) + a glassy elevated card,
 * so the auth flow reads like the rest of the revamped product.
 *
 * It only owns CHROME — the caller passes its existing <Form>/content as
 * children, fully untouched. Handlers, Formik, Turnstile, redux and
 * redirects stay in the page.
 *
 * Props:
 *  - heading      card title (e.g. "Sign In")
 *  - subheading   muted line under the card title
 *  - icon         optional node rendered above the heading (e.g. a lord-icon)
 *  - strapline    line under the logo (defaults to "Less Work. Do More.")
 *  - width        card column width: "sm" | "md" (default) — md = md8/lg6/xl5
 *  - below        node rendered under the card (e.g. "Don't have an account?")
 *  - children     the form / body
 */
const AuthShell = ({
  heading,
  subheading,
  icon,
  strapline = null,
  width = "md",
  below,
  children,
}) => {
  const { isBrandingActive } = useBranding();

  return (
    <div className="auth-shell">
      <div className="auth-shell__bg" aria-hidden="true">
        <span className="auth-shell__glow auth-shell__glow--1" />
        <span className="auth-shell__glow auth-shell__glow--2" />
        <span className="auth-shell__glow auth-shell__glow--3" />
      </div>

      <div className="auth-shell__content">
        <Container>
          <div
            className={`auth-shell__card-wrap auth-shell__card-wrap--${width}`}
          >
            <div className="auth-shell__card">
              <div className="auth-shell__card-logo">
                <Link to="/">
                  <img src={logoDark} alt="CRMit" height="42" />
                </Link>
              </div>

              {(icon || heading) && (
                <div className="auth-shell__card-head">
                  {icon && <div className="auth-shell__card-icon">{icon}</div>}
                  {heading && (
                    <h5 className="auth-shell__card-title">{heading}</h5>
                  )}
                  {subheading && (
                    <p className="auth-shell__card-sub">{subheading}</p>
                  )}
                </div>
              )}

              {strapline && (
                <p className="auth-shell__strapline">
                  {isBrandingActive && "CRMit — "}
                  {strapline}
                </p>
              )}

              <div className="auth-shell__card-body">{children}</div>
            </div>

            {below && <div className="auth-shell__below">{below}</div>}
          </div>
        </Container>
      </div>

      <footer className="auth-shell__footer">
        <p className="mb-0">
          &copy; {new Date().getFullYear()}
          {isBrandingActive && " CRMit."} Crafted with{" "}
          <i className="mdi mdi-heart text-danger" />
          {isBrandingActive && " by CRMit"}
        </p>
      </footer>
    </div>
  );
};

AuthShell.propTypes = {
  heading: PropTypes.node,
  subheading: PropTypes.node,
  icon: PropTypes.node,
  strapline: PropTypes.node,
  width: PropTypes.oneOf(["sm", "md"]),
  below: PropTypes.node,
  children: PropTypes.node,
};

export default AuthShell;
